import React, { JSX } from 'react';
import Keys from '../Translations/generated/search-content.de.json.keys';
import fallback from '../Translations/generated/search-content.de.json';
import { Price, useTranslation } from '@ibe/components';
import { Button } from 'reactstrap';
import { ApiImage, ApiProductCard } from '../../api';
import { sanitize } from '@ibe/services';

const imageWidthMapper: { [key: string]: string } = {
  XS: '480',
  S: '768',
  L: '1280',
  XL: '1920'
};

const getSrcSet = (srcSet: ApiImage['srcSet']): string =>
  Object.entries(srcSet).reduce((total: string, [key, value]: [string, string], idx: number) => {
    return `${total}${idx !== 0 ? ', ' : ''}${value} ${imageWidthMapper[key]}w`;
  }, '');

const ResultListItem = ({
  onClick,
  className,
  productResultItem
}: {
  onClick: () => void;
  className?: string;
  productResultItem: ApiProductCard;
}): JSX.Element => {
  const { t } = useTranslation('search-content', fallback);
  const {
    name,
    description,
    partnerText,
    partnerImage,
    price,
    strikeThroughPrice,
    suppressStrikePrices,
    context,
    dateInfo,
    markerImage,
    image,
    groupTypeImage
  } = productResultItem;

  return (
    <div onClick={onClick} className={`product-teaser card`}>
      <div className="product-teaser__image card-img-top">
        <div className="product-teaser__image__inner">
          {!!image && !!image?.srcSet ? (
            <img
              className="w-100 h-100 product-teaser__image__main"
              alt={image.title}
              src={image.srcSet.XS}
              srcSet={getSrcSet(image.srcSet)}
              sizes="(max-width: 768px) 100vw,(max-width: 1200px) 50vw, 25vw"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-100 h-100 product-teaser__image__main__placeholder" />
          )}
        </div>
        {!!markerImage?.srcSet?.XS && (
          <span className="product-teaser__markerImage__container">
            <img
              className="product-teaser__markerImage"
              alt={markerImage.title || ''}
              title={markerImage.title || ''}
              src={markerImage.srcSet.XS}
            />
          </span>
        )}
        {!!groupTypeImage?.srcSet?.XS && (
          <img
            className="product-teaser__smallGroupImage"
            alt={groupTypeImage.title || ''}
            title={groupTypeImage.title || ''}
            src={groupTypeImage.srcSet.XS}
          />
        )}
      </div>
      <div className="product-teaser__body__outer">
        <div className="product-teaser__body card-body">
          <div>
            <div
              className="product-teaser__name"
              dangerouslySetInnerHTML={{ __html: sanitize(name) }}
            ></div>
            <div
              className="product-teaser__description"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            ></div>
          </div>

          <div className="product-teaser__body__lower">
            <div className="product-teaser__date row">
              <div className="col">
                {!!partnerText && (
                  <div className="product-teaser__partner__headline">{partnerText}</div>
                )}
                {!!partnerImage?.srcSet?.XS && (
                  <img
                    className="product-teaser__partner__image"
                    alt={partnerImage.title || ''}
                    title={partnerImage.title || ''}
                    src={partnerImage.srcSet.XS}
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>

              <div className="col">
                {!!dateInfo && (
                  <div className="product-teaser__date__headline">{t(Keys.dates)}</div>
                )}
                {!!dateInfo && <div className="product-teaser__date__text">{dateInfo}</div>}
              </div>
            </div>
            <div>
              {!!strikeThroughPrice?.amount && (
                <div
                  className={`product-teaser__wasPrice product-teaser__price${
                    suppressStrikePrices ?? false ? ' noStrikeThrough' : ''
                  }`}
                >
                  {t(Keys.insteadOfFrom)}&nbsp;
                  <span>
                    <Price
                      price={{
                        finalPrice: strikeThroughPrice.amount,
                        basePrice: strikeThroughPrice.amount,
                        currencyCode: strikeThroughPrice.currencyCode || '',
                        modifiers: [],
                        isInclusive: false,
                        isStartingAt: false
                      }}
                      displayInline
                      roundPrice="ROUND_DOWN_MODE"
                      useGrouping={false}
                      removeZeroDecimals
                      currencyFirst
                    />
                  </span>
                </div>
              )}

              <div className="product-teaser__price">
                <span className="product-teaser__price--text">{t(Keys.nowFrom)}&nbsp;</span>
                <span
                  className={`product-teaser__price--amount${
                    !strikeThroughPrice?.amount ? '' : ' product-teaser__price--amount--strike'
                  }`}
                >
                  {!!price?.amount && (
                    <Price
                      price={{
                        finalPrice: price.amount,
                        basePrice: price.amount,
                        currencyCode: price.currencyCode || '',
                        modifiers: [],
                        isInclusive: false,
                        isStartingAt: false
                      }}
                      displayInline
                      useGrouping={false}
                      removeZeroDecimals
                      currencyFirst
                    />
                  )}
                </span>
              </div>
            </div>
            <div className="product-teaser__context">
              <span>{`${t(Keys.perPerson)}${!!context ? `, ${context}` : ''}`}</span>
            </div>

            <Button className="product-teaser__cta" color="primary">
              {t(Keys.goToOffer)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultListItem;
